import React from 'react';
import { TrendingUp } from 'lucide-react';
import { cn } from '../../../utils/cn';
import { FeatureCard } from '../FeatureCard';
import type { StatCardProps, StatCardColor } from './StatCard.types';

// Color hex mapping for FeatureCard
const colorHexMap: Record<StatCardColor, string> = {
  cyan: '#06b6d4',
  green: '#22c55e',
  red: '#ef4444',
  yellow: '#eab308',
  teal: '#14b8a6',
  gray: '#6b7280',
  orange: '#ac6d00',
};

/**
 * StatCard - Simplified wrapper around FeatureCard for statistics display.
 *
 * Uses FeatureCard internally with trend indicator as children.
 */
export const StatCard: React.FC<StatCardProps> = ({
  label,
  value,
  icon,
  color = 'cyan',
  activeColor,
  isActive = false,
  onClick,
  trend,
  className,
  iconClassName,
}) => {
  const hexColor = activeColor || colorHexMap[color];

  return (
    <FeatureCard
      label={label}
      value={String(value)}
      icon={icon}
      color={hexColor}
      onClick={onClick}
      isActive={isActive}
      className={className}
      iconClassName={iconClassName}
    >
      {/* Trend Indicator */}
      {trend && (
        <div className="flex items-center gap-1.5 mt-2">
          <div
            className={cn(
              'p-0.5 rounded-full',
              trend.type === 'up'
                ? 'bg-green-500/10'
                : trend.type === 'down'
                  ? 'bg-red-500/10'
                  : 'bg-gray-500/10',
            )}
          >
            <TrendingUp
              size={12}
              className={cn(
                'transition-transform',
                trend.type === 'up'
                  ? 'text-green-500'
                  : trend.type === 'down'
                    ? 'text-red-500 rotate-180'
                    : 'text-gray-400',
              )}
            />
          </div>
          <span
            className={cn(
              'text-xs font-bold tabular-nums',
              trend.type === 'up'
                ? 'text-green-500'
                : trend.type === 'down'
                  ? 'text-red-500'
                  : 'text-gray-400',
            )}
          >
            {trend.value}
          </span>
        </div>
      )}
    </FeatureCard>
  );
};

export default StatCard;
